import React from 'react';
import { CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
interface PricingSectionProps {
  variant?: 'default' | 'beta';
}
export function PricingSection({
  variant = 'default'
}: PricingSectionProps) {
  const isBeta = variant === 'beta';
  return <section id="pricing" className="py-20 bg-white" data-id="element-574">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-575">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-576">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-577">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600" data-id="element-578">
            Start free and upgrade when you're ready for advanced features.
          </p>
        </div>

        {/* Pricing tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-579">
          {/* Free Plan */}
          <div className="rounded-xl border border-gray-200 shadow p-8 flex flex-col" data-id="element-580">
            <div className="mb-6" data-id="element-581">
              <h3 className="text-2xl font-bold text-[#5e60ce] mb-2" data-id="element-582">
                Free Plan
              </h3>
              <p className="text-gray-600" data-id="element-583">Perfect to get started</p>
            </div>
            <div className="mb-6" data-id="element-584">
              <span className="text-4xl font-bold text-gray-900" data-id="element-585">$0</span>
              <span className="text-gray-600" data-id="element-586">/mo</span>
            </div>
            <ul className="mb-8 space-y-3 flex-grow" data-id="element-587">
              <li className="flex items-start" data-id="element-588">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-589" />
                <span className="text-gray-700 flex items-center flex-wrap" data-id="element-590">
                  {isBeta ? 'Access to Newsfiliate' : 'Access to deal database'}
                  {isBeta && <Link to="/products/newsfiliate" className="ml-2 text-[#5e60ce] hover:text-[#4a4db0] text-sm underline" data-id="element-591">
                      Learn More
                    </Link>}
                </span>
              </li>
              <li className="flex items-start" data-id="element-592">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-593" />
                <span className="text-gray-700" data-id="element-594">Amazon product boxes</span>
              </li>
              <li className="flex items-start" data-id="element-595">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-596" />
                <span className="text-gray-700" data-id="element-597">
                  Automatic affiliate linking
                </span>
              </li>
              {!isBeta && <li className="flex items-start" data-id="element-598">
                  <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-599" />
                  <span className="text-gray-700" data-id="element-600">Custom branding options</span>
                </li>}
            </ul>
            <a href="https://app.kit.com/apps/1229" className="inline-flex items-center justify-center px-6 py-3 border border-[#5e60ce] rounded-md text-base font-medium text-[#5e60ce] bg-white hover:bg-gray-50" target="_blank" rel="noopener noreferrer" data-id="element-601">
              Get Started Free
            </a>
          </div>

          {/* Pro Plan */}
          <div className="rounded-xl border-2 border-[#5e60ce] shadow-xl p-8 flex flex-col relative" data-id="element-602">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2" data-id="element-603">
              <span className="bg-[#5e60ce] text-white px-4 py-1 rounded-full text-sm font-semibold" data-id="element-604">
                {isBeta ? 'Beta' : 'Most Popular'}
              </span>
            </div>
            <div className="mb-6" data-id="element-605">
              <h3 className="text-2xl font-bold text-[#5e60ce] mb-2" data-id="element-606">
                Pro Plan
              </h3>
              <p className="text-gray-600" data-id="element-607">For serious creators</p>
            </div>
            <div className="mb-6" data-id="element-608">
              <span className="text-4xl font-bold text-gray-900" data-id="element-609">$5.99</span>
              <span className="text-gray-600" data-id="element-610">/mo</span>
            </div>
            <ul className="mb-8 space-y-3 flex-grow" data-id="element-611">
              <li className="flex items-start" data-id="element-612">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-613" />
                <span className="text-gray-700" data-id="element-614">Everything in Free Plan</span>
              </li>
              <li className="flex items-start" data-id="element-615">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-616" />
                <span className="text-gray-700" data-id="element-617">Priority Support</span>
              </li>
              <li className="flex items-start" data-id="element-618">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-619" />
                <span className="text-gray-700 flex items-center flex-wrap" data-id="element-620">
                  Intelligent Affiliate Monetization
                  {!isBeta && <Link to="/automated-affiliate-monetization" className="ml-2 text-[#5e60ce] hover:text-[#4a4db0] text-sm underline" data-id="element-621">
                      Learn more
                    </Link>}
                </span>
              </li>
            </ul>
            {isBeta ? <button className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md text-base font-medium text-white hover:bg-opacity-90" style={{
            backgroundColor: '#5e60ce'
          }} data-id="element-622">
                Join the Beta
              </button> : <a href="https://app.kit.com/apps/1229" className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md text-base font-medium text-white hover:bg-opacity-90" style={{
            backgroundColor: '#5e60ce'
          }} target="_blank" rel="noopener noreferrer" data-id="element-623">
                Upgrade to Pro
              </a>}
          </div>
        </div>
      </div>
    </section>;
}